// src/pages/Workspace.js
import React, { useEffect, useState, useMemo } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Stack,
  Tabs,
  Tab,
  IconButton,
  LinearProgress,
  Avatar,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  Button,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import { useLocation, useNavigate, Link as RouterLink } from "react-router-dom";
import axios from "axios";

import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import BuildRoundedIcon from "@mui/icons-material/BuildRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";

import TrainingInterface from "../components/TrainingInterface";
import ChatInterface from "../components/ChatInterface";
import ThemeToggleButton from "../components/ThemeToggleButton";
import PlanBadge from "../components/PlanBadge";
import { useTheme } from "@mui/material/styles";
import { getMe } from "../api/billing";

// Optional: if you have AuthContext, keep this
import { useAuth } from "../auth/AuthContext";

const API_BASE = process.env.REACT_APP_API;

/* Prominent, pill tabs */
const StyledTabs = styled(Tabs)(({ theme }) => ({
  borderRadius: 9999,
  padding: 4,
  minHeight: 0,
  background:
    theme.palette.mode === "light"
      ? alpha(theme.palette.primary.main, 0.08)
      : alpha(theme.palette.primary.main, 0.16),
  boxShadow:
    theme.palette.mode === "light"
      ? "0 2px 6px rgba(0,0,0,0.06)"
      : "0 2px 8px rgba(0,0,0,0.28)",
  "& .MuiTabs-flexContainer": { gap: 8 },
  "& .MuiTabs-indicator": { display: "none" },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  minHeight: 0,
  height: 48,
  borderRadius: 9999,
  textTransform: "none",
  fontWeight: 800,
  fontSize: "0.975rem",
  padding: "10px 18px",
  color: theme.palette.text.secondary,
  letterSpacing: 0.2,
  "& .MuiTab-iconWrapper": { marginRight: 10, fontSize: 22 },
  "&.Mui-selected": {
    color: theme.palette.getContrastText(theme.palette.primary.main),
    backgroundColor: theme.palette.primary.main,
    boxShadow:
      theme.palette.mode === "light"
        ? "0 2px 6px rgba(37,99,235,0.35)"
        : "0 3px 10px rgba(37,99,235,0.45)",
    border: `1px solid ${alpha(theme.palette.common.white, 0.18)}`,
  },
  "&:hover": {
    backgroundColor:
      theme.palette.mode === "light"
        ? alpha(theme.palette.primary.main, 0.12)
        : alpha(theme.palette.primary.main, 0.22),
  },
}));

/* Helper: initials + deterministic color */
function stringToHslColor(str = "", s = 65, l = 55) {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const h = Math.abs(hash) % 360;
  return `hsl(${h} ${s}% ${l}%)`;
}
function initialsFromEmail(email) {
  const local = String(email || "?").split("@")[0] || "?";
  const parts = local.replace(/[^a-zA-Z0-9]+/g, " ").trim().split(" ");
  if (parts.length === 1) return (parts[0][0] || "?").toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export default function Workspace() {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();

  const { user, signOut } = useAuth(); // { email, sub, ... }

  const [activeTab, setActiveTab] = useState(0);
  const [scrolled, setScrolled] = useState(false);

  // assistant identity
  const [assistantId, setAssistantId] = useState(
    () => localStorage.getItem("assistantId") || ""
  );
  const [assistantName, setAssistantName] = useState("");

  // status coming from TrainingInterface (we still use these for progress bar)
  const [headerStatus, setHeaderStatus] = useState({
    isTraining: false,
    isFetching: false,
    hasChangesToTrain: false,
    anyTrained: false,
  });

  // Profile menu
  const [anchorElProfile, setAnchorElProfile] = useState(null);
  const openProfileMenu = Boolean(anchorElProfile);

  const avatarBg = useMemo(
    () => stringToHslColor(user?.email || user?.sub || "user"),
    [user]
  );
  const avatarText = useMemo(
    () => (user?.email ? initialsFromEmail(user.email) : "U"),
    [user]
  );

  const [me, setMe] = useState(null);

  // Read assistantId from query on first load
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fromUrl = params.get("assistantId");
    if (fromUrl) {
      setAssistantId(fromUrl);
      localStorage.setItem("assistantId", fromUrl);
    }
  }, [location.search]);

  // Persist assistantId
  useEffect(() => {
    if (assistantId) localStorage.setItem("assistantId", assistantId);
  }, [assistantId]);

  // Fetch assistant name when assistantId changes (still used in Training details, not header)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!assistantId) {
        setAssistantName("");
        return;
      }
      try {
        const res = await axios.get(`${API_BASE}/assistants/${assistantId}`);
        if (!cancelled) setAssistantName(res.data?.Name || "");
      } catch {
        if (!cancelled) setAssistantName("");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [assistantId]);

  // Fetch plan
  useEffect(() => {
    (async () => {
      if (user?.sub) {
        try {
          setMe(await getMe(user.sub));
        } catch {}
      }
    })();
  }, [user?.sub]);

  // Floating header shadow when scrolling
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 2);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleGoToChat = () => setActiveTab(1);
  const logoSrc =
    theme.palette.mode === "dark"
      ? "/brand/SemiQolon.png"
      : "/brand/SemiQolon-black.png";

  return (
    <Box sx={{ width: "100%" }}>
      {/* Header */}
      <AppBar
        position="sticky"
        color="inherit"
        elevation={scrolled ? 3 : 0}
        sx={(t) => ({
          backdropFilter: "saturate(1.1) blur(6px)",
          background: alpha(
            t.palette.background.paper,
            t.palette.mode === "light" ? 0.9 : 0.8
          ),
          borderBottom: "1px solid",
          borderColor: "divider",
        })}
      >
        <Toolbar
          sx={{
            gap: 1.25,
            minHeight: 56,
            position: "relative", // for absolute-centered tabs
          }}
        >
          {/* Left: back + brand */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton edge="start" onClick={() => navigate("/studio")}>
              <ArrowBackRoundedIcon />
            </IconButton>
            <Box
              component="img"
              src={logoSrc}
              alt="SemiQolon Studio"
              sx={{ height: 32, width: "auto", display: "block" }}
              loading="eager"
            />
          </Box>

          {/* Center: tabs (dead center; absolute to avoid shifting with right-side content) */}
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              top: "50%",
              translate: "0 -50%",
            }}
          >
            <StyledTabs
              value={activeTab}
              onChange={(_e, v) => setActiveTab(v)}
              aria-label="Workspace tabs"
            >
              <StyledTab
                icon={<BuildRoundedIcon />}
                iconPosition="start"
                label="Training"
              />
              <StyledTab
                icon={<ForumRoundedIcon />}
                iconPosition="start"
                label="Chat"
              />
            </StyledTabs>
          </Box>

          {/* Right: theme, plan, upgrade, profile */}
          <Box sx={{ ml: "auto" }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <ThemeToggleButton />
              {me?.plan && <PlanBadge plan={me.plan} />}
              <Button
                component={RouterLink}
                to="/pricing"
                size="small"
                variant="outlined"
              >
                Upgrade
              </Button>
              <Tooltip title={user?.email || "Profile"}>
                <IconButton
                  onClick={(e) => setAnchorElProfile(e.currentTarget)}
                  sx={{ ml: 0.5 }}
                  size="small"
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: avatarBg,
                      fontSize: 14,
                      fontWeight: 800,
                      color: "#fff",
                    }}
                  >
                    {avatarText}
                  </Avatar>
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
        </Toolbar>

        {(headerStatus.isFetching || headerStatus.isTraining) && <LinearProgress />}
      </AppBar>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorElProfile}
        open={openProfileMenu}
        onClose={() => setAnchorElProfile(null)}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem
          onClick={() => {
            setAnchorElProfile(null);
            navigate("/profile");
          }}
        >
          <ListItemIcon>
            <AccountCircleRoundedIcon fontSize="small" />
          </ListItemIcon>
          Profile
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchorElProfile(null);
            if (typeof signOut === "function") {
              signOut();
            }
            navigate("/login");
          }}
        >
          <ListItemIcon>
            <LogoutRoundedIcon fontSize="small" />
          </ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>

      {/* Keep both mounted so state persists */}
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: activeTab === 0 ? "block" : "none" }}>
          <TrainingInterface
            assistantId={assistantId}
            onAssistantReady={(id) => setAssistantId(id)}
            onGoToChat={handleGoToChat}
            hideHeader
            onStatusChange={(s) =>
              setHeaderStatus((prev) => ({ ...prev, ...s }))
            }
            isPremium = {me?.plan === "PLATINUM" || me?.plan === "PREMIUM" ? true : false}
            userPlan = {me?.plan}
          />
        </Box>

        <Box sx={{ display: activeTab === 1 ? "block" : "none" }}>
          {/* Pass the signed-in user id to Chat */}
          <ChatInterface assistantId={assistantId} hideHeader userId={user?.sub} initial={avatarText} isActiveTab={activeTab === 1}/>
        </Box>
      </Box>
    </Box>
  );
}
